import styled from '@emotion/styled';
import { ob } from '../onboardingV2.tokens';

export const SelectedCount = styled.p`
    margin: 0 0 10px;
    font-size: 13px;
    color: ${ob.color.body};

    strong {
        color: ${ob.color.brand};
    }
`;

export const Empty = styled.p`
    margin: 16px 0;
    text-align: center;
    font-size: 14px;
    color: ${ob.color.muted};
`;

export const SelectedBlock = styled.div`
    margin-top: 18px;
`;

export const SelectedTitle = styled.p`
    margin: 0 0 8px;
    font-size: 13px;
    font-weight: 700;
    color: ${ob.color.label};
`;

export const SelectedRow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 0;
    border-top: 1px solid ${ob.color.borderSubtle};

    .label {
        flex: 1;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: 14px;
        color: ${ob.color.text};
    }
`;

export const CatThumb = styled.button`
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    border: 1px dashed ${(props) => (props.hasPhoto ? 'transparent' : ob.color.border)};
    background:
        ${(props) => (props.src ? `center / cover no-repeat url(${props.src}),` : '')}
        ${(props) => (props.hasPhoto ? ob.color.subtle : ob.color.lime50)};
    color: ${ob.color.brand};
    font-size: 18px;
    cursor: pointer;

    &:focus-visible {
        outline: none;
        box-shadow: 0 0 0 3px ${ob.color.lime200};
    }
`;

export const PhotoAction = styled.button`
    flex-shrink: 0;
    background: none;
    border: 0;
    padding: 4px 6px;
    font-size: 13px;
    font-weight: 600;
    color: ${ob.color.brand};
    cursor: pointer;

    &:disabled {
        color: ${ob.color.disabledText};
        cursor: default;
    }
`;

export const AddForm = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 12px;
    padding: 12px;
    border: 1px solid ${ob.color.borderSubtle};
    border-radius: ${ob.radius.md};
    background: ${ob.color.subtle};

    input[type='text'] {
        flex: 1;
        min-width: 0;
        height: 40px;
        padding: 0 12px;
        border: 1px solid ${ob.color.border};
        border-radius: ${ob.radius.sm};
        font-size: 16px;
        outline: none;

        &:focus {
            border-color: ${ob.color.lime};
            box-shadow: 0 0 0 3px ${ob.color.lime200};
        }
    }
`;

/** Subcategories of a selected category, indented under its row (thumb 44px + gap 12px). */
export const SubList = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    padding: 0 0 10px 56px;
`;

export const SubChip = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 2px;
    max-width: 100%;
    padding: 4px 4px 4px 10px;
    border-radius: ${ob.radius.pill};
    background: ${ob.color.lime50};
    border: 1px solid ${ob.color.limeBorder};
    font-size: 12.5px;
    color: ${ob.color.text};

    > span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    > button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 22px;
        height: 22px;
        border: 0;
        border-radius: 50%;
        background: none;
        color: ${ob.color.muted};
        font-size: 14px;
        cursor: pointer;

        &:focus-visible {
            outline: none;
            box-shadow: 0 0 0 2px ${ob.color.lime200};
        }
    }
`;

export const SubLink = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 2px;
    border: 0;
    background: none;
    font-size: 12.5px;
    font-weight: 600;
    color: ${(props) => (props.muted ? ob.color.muted : ob.color.brand)};
    cursor: ${(props) => (props.muted ? 'default' : 'pointer')};
`;

export const SubForm = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    flex: 1 1 100%;

    input[type='text'] {
        flex: 1;
        min-width: 0;
        height: 36px;
        padding: 0 10px;
        border: 1px solid ${ob.color.border};
        border-radius: ${ob.radius.sm};
        font-size: 16px;
        outline: none;

        &:focus {
            border-color: ${ob.color.lime};
            box-shadow: 0 0 0 3px ${ob.color.lime200};
        }
    }
`;

export const PhotoErrorText = styled.p`
    margin: -2px 0 8px 56px;
    font-size: 12.5px;
    color: ${ob.color.error};
`;

/** The category name; a button so it can be renamed in place. */
export const NameButton = styled.button`
    flex: 1;
    min-width: 0;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 0;
    border: 0;
    background: none;
    text-align: left;
    font-size: 14px;
    color: ${ob.color.text};
    cursor: text;

    span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    i {
        flex-shrink: 0;
        font-size: 14px;
        color: ${ob.color.muted};
    }
`;

export const RenameInput = styled.input`
    flex: 1;
    min-width: 0;
    height: 36px;
    padding: 0 10px;
    border: 1px solid ${ob.color.lime};
    border-radius: ${ob.radius.sm};
    font-size: 16px;
    outline: none;
    box-shadow: 0 0 0 3px ${ob.color.lime200};
`;

/** Suggested subcategory not added yet: tap to add. */
export const SuggestedSub = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 2px;
    padding: 4px 10px;
    border-radius: ${ob.radius.pill};
    border: 1px dashed ${ob.color.border};
    background: ${ob.color.surface};
    font-size: 12px;
    color: ${ob.color.body};
    cursor: pointer;

    &:hover {
        border-color: ${ob.color.borderHover};
    }
`;

export const PickTitle = styled.p`
    margin: 22px 0 10px;
    font-size: 13px;
    font-weight: 700;
    color: ${ob.color.label};
`;
